import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { 
  auth,
} from "../config/firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from "firebase/auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // renamed for consistency
  const [error, setError] = useState(null);
  
  // Monitor auth state changes
  useEffect(() => {
    console.log("AuthProvider - Setting up auth state listener");
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("AuthProvider - Auth state changed:", { 
        hasUser: !!user,
        timestamp: new Date().toISOString()
      });
      
      if (user) {
        try {
          // Transform Firebase user into your app's user format
          const userData = {
            uid: user.uid,
            email: user.email,
            name: user.displayName || "Usuario Wemax",
            photoURL: user.photoURL || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
            emailVerified: user.emailVerified,
            // Add additional user properties that might be expected by components
            preferences: {
              theme: 'dark',
              notifications: {
                email: true,
                push: true,
                updates: false
              },
              language: 'es',
              dateFormat: 'DD/MM/YYYY'
            },
            // Default professional info
            role: "Usuario Wemax",
            company: "",
            skills: [],
            location: "",
            socialLinks: {
              website: "",
              github: "",
              linkedin: "",
              twitter: ""
            }
          };
          
          console.log("AuthProvider - User data transformed:", {
            uid: userData.uid,
            email: userData.email,
            name: userData.name,
            hasPhoto: !!userData.photoURL
          });
          
          setUser(userData);
        } catch (error) {
          console.error("AuthProvider - Error transforming user data:", error);
          setError("Error al procesar los datos del usuario");
        }
      } else {
        console.log("AuthProvider - No user found, clearing user state");
        setUser(null);
      }
      setIsLoading(false);
    });

    // Cleanup subscription
    return () => {
      console.log("AuthProvider - Cleaning up auth state listener");
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      console.log("AuthProvider - Attempting login with email/password");
      setError(null);
      setIsLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("AuthProvider - Email login successful");
      
      const userData = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        name: userCredential.user.displayName || "Usuario Wemax",
        photoURL: userCredential.user.photoURL || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
        emailVerified: userCredential.user.emailVerified,
        // Add default properties
        preferences: {
          theme: 'dark',
          notifications: {
            email: true,
            push: true,
            updates: false
          },
          language: 'es',
          dateFormat: 'DD/MM/YYYY'
        }
      };
      setUser(userData);
      return userData;
    } catch (err) {
      console.error("AuthProvider - Login error:", err);
      setError(handleFirebaseError(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  const loginWithGoogle = useCallback(async () => {
    try {
      console.log("AuthProvider - Attempting Google login");
      setError(null);
      setIsLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log("AuthProvider - Google login successful");
      
      const userData = {
        uid: result.user.uid,
        email: result.user.email,
        name: result.user.displayName || "Usuario Wemax",
        photoURL: result.user.photoURL || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
        emailVerified: result.user.emailVerified,
        // Add default properties
        preferences: {
          theme: 'dark',
          notifications: {
            email: true,
            push: true,
            updates: false
          },
          language: 'es',
          dateFormat: 'DD/MM/YYYY'
        }
      };
      setUser(userData);
      return userData;
    } catch (err) {
      console.error("AuthProvider - Google login error:", err);
      setError(handleFirebaseError(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  const logout = useCallback(async () => {
    try {
      console.log("AuthProvider - Logging out user");
      setIsLoading(true);
      await signOut(auth);
      setUser(null);
      console.log("AuthProvider - User logged out successfully");
    } catch (err) {
      console.error("AuthProvider - Error during logout:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper function to handle Firebase error messages
  const handleFirebaseError = (error) => {
    switch (error.code) {
      case "auth/user-not-found":
        return "No existe una cuenta con este correo electrónico.";
      case "auth/wrong-password":
        return "Contraseña incorrecta.";
      case "auth/invalid-email":
        return "El correo electrónico no es válido.";
      case "auth/user-disabled":
        return "Esta cuenta ha sido deshabilitada.";
      case "auth/popup-closed-by-user":
        return "Inicio de sesión cancelado.";
      default:
        return error.message;
    }
  };

  // Function to update user photo URL
  const updateUserPhoto = useCallback((photoURL) => {
    console.log("AuthProvider - Updating user photo URL");
    if (user) {
      setUser(prevUser => ({
        ...prevUser,
        photoURL: photoURL,
      }));
      console.log("AuthProvider - User photo URL updated to:", photoURL);
    }
  }, [user]);

  // Function to update user preferences
  const updateUserPreferences = useCallback((preferences) => {
    console.log("AuthProvider - Updating user preferences", preferences);
    if (user) {
      setUser(prevUser => ({
        ...prevUser,
        preferences: {
          ...prevUser.preferences,
          ...preferences
        }
      }));
      console.log("AuthProvider - User preferences updated");
    }
  }, [user]);
  
  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading, // renamed from loading for consistency
      error, 
      login, 
      loginWithGoogle, 
      logout,
      updateUserPhoto,
      updateUserPreferences
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
